/**
 * WorkflowsPage — l'écran « Circuits » (vague 4a, mig 244).
 *
 * Le maire/DGS y fabrique les parcours de sa commune : il DUPLIQUE un modèle
 * éditeur (décision D1) puis adapte SA copie — renomme une étape, change qui
 * valide, exige une pièce. Le verrou est raconté tel qu'il est : dès qu'un
 * parcours vit, ses états ne se suppriment plus (on désactive, on ne casse
 * pas l'en-cours).
 */

import { useCallback, useEffect, useState } from 'react';
import { api, ApiException, type WorkflowDefinition } from '@/lib/api';
import { Card, Button, Badge, Alert, Field } from '@/components/ui';
import { useConfirmDialog } from '@/lib/use-confirm-dialog';
import {
  GitBranch, Plus, ArrowLeft, ArrowRight, Loader2, AlertCircle, Check,
  Trash2, Copy, Lock, Info,
} from 'lucide-react';
import { CircuitEditeurVisuel } from '@/components/circuits/CircuitEditeurVisuel';
import { CircuitSimulateur } from '@/components/circuits/CircuitSimulateur';

const CHAMP = 'h-10 w-full rounded-r-lg border border-border bg-surface/60 px-3 text-sm text-ink focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent';

const ROLES: Array<{ v: string; l: string }> = [
  { v: 'agent', l: 'Agent et au-dessus' },
  { v: 'manager', l: 'Manager et au-dessus' },
  { v: 'admin', l: 'Admin uniquement' },
];
const SERVICES: Array<{ v: string; l: string }> = [
  { v: '', l: '— Tous les services —' },
  { v: 'service_technique', l: 'Service technique' },
  { v: 'scolaire_cantine', l: 'Scolaire / cantine' },
  { v: 'petite_enfance', l: 'Petite enfance' },
  { v: 'urbanisme', l: 'Urbanisme' },
  { v: 'associations', l: 'Associations' },
  { v: 'grc', l: 'GRC' },
  { v: 'rh', l: 'RH' },
  { v: 'comptable', l: 'Comptabilité' },
];

export function WorkflowsPage() {
  const [definitions, setDefinitions] = useState<WorkflowDefinition[]>([]);
  const [presets, setPresets] = useState<Array<{ code: string; libelle: string; description: string; etats: number; transitions: number }>>([]);
  // Vague 4e — configurateur d'envoi d'une flèche (10.4).
  const [envoiFleche, setEnvoiFleche] = useState<string | null>(null);
  const [ecCanal, setEcCanal] = useState<'interne' | 'email' | 'sms'>('interne');
  const [ecSujet, setEcSujet] = useState('');
  const [ecCorps, setEcCorps] = useState('');
  const [ecValidation, setEcValidation] = useState(true);
  const [ecValidateurs, setEcValidateurs] = useState<string[]>([]);
  const [encadrants, setEncadrants] = useState<Array<{ id: string; label: string; role: string }>>([]);

  useEffect(() => {
    api.workflows.annuaireDestinataires()
      .then((a) => setEncadrants(a.users.filter((u) => u.role === 'manager' || u.role === 'admin')))
      .catch(() => setEncadrants([]));
  }, []);
  const [ouvert, setOuvert] = useState<WorkflowDefinition | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [choixPreset, setChoixPreset] = useState(false);
  const { confirm, dialogElement } = useConfirmDialog();

  // Formulaire nouvelle transition
  const [trDe, setTrDe] = useState('');
  const [trVers, setTrVers] = useState('');
  const [trLibelle, setTrLibelle] = useState('');
  const [trRole, setTrRole] = useState('manager');
  const [trService, setTrService] = useState('');
  const [trPiece, setTrPiece] = useState(false);

  const charger = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [d, p] = await Promise.all([api.workflows.definitions(), api.workflows.presets()]);
      setDefinitions(d.definitions);
      setPresets(p.presets);
      setOuvert((prev) => prev ? d.definitions.find((x) => x.id === prev.id) ?? null : null);
    } catch (e) {
      setError(e instanceof ApiException ? e.payload.error : 'Chargement impossible');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { void charger(); }, [charger]);

  async function action(fn: () => Promise<unknown>, ok?: string) {
    setError(null); setMsg(null);
    try {
      await fn();
      if (ok) setMsg(ok);
      await charger();
    } catch (e) {
      setError(e instanceof ApiException ? e.payload.error : 'Action impossible');
    }
  }

  if (loading && !definitions.length) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-ink-muted" aria-hidden="true" />
      </div>
    );
  }

  // ── Vue détail d'un parcours ───────────────────────────────────────────────
  if (ouvert) {
    const enCours = ouvert.instances > 0;
    const etatDe = (id: string) => ouvert.etats.find((e) => e.id === id);
    return (
      <div className="space-y-3">
        {dialogElement}
        {error && <Alert variant="error" icon={AlertCircle}>{error}</Alert>}
        {msg && <Alert variant="success" icon={Check}>{msg}</Alert>}

        <button type="button" onClick={() => { setOuvert(null); setMsg(null); setError(null); }}
          className="inline-flex items-center gap-1.5 text-sm text-ink-muted hover:text-ink">
          <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Tous les circuits
        </button>

        <Card variant="glass" padded>
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="min-w-0">
              <input value={ouvert.libelle}
                onChange={(e) => setOuvert({ ...ouvert, libelle: e.target.value })}
                onBlur={() => void action(() => api.workflows.modifier(ouvert.id, { libelle: ouvert.libelle }))}
                className="w-full bg-transparent text-base font-semibold text-ink focus:outline-none" />
              <p className="mt-0.5 text-xs text-ink-muted">{ouvert.description}</p>
            </div>
            <div className="flex flex-shrink-0 items-center gap-1.5">
              {ouvert.presetSource && <Badge variant="info" size="sm">Modèle : {ouvert.presetSource}</Badge>}
              {enCours && <Badge variant="warning" size="sm">{ouvert.instances} en cours</Badge>}
              <Badge variant={ouvert.actif ? 'success' : 'neutral'} size="sm">
                {ouvert.actif ? 'Actif' : 'Désactivé'}
              </Badge>
            </div>
          </div>
          <div className="mt-3 flex flex-wrap gap-1.5">
            <Button variant="secondary" size="sm"
              onClick={() => void action(() => api.workflows.modifier(ouvert.id, { actif: !ouvert.actif }),
                ouvert.actif ? 'Circuit désactivé — les parcours en cours continuent.' : 'Circuit réactivé.')}>
              {ouvert.actif ? 'Désactiver' : 'Réactiver'}
            </Button>
            <Button variant="ghost" size="sm"
              onClick={() => void action(() => api.workflows.dupliquer(ouvert.id), 'Copie créée.')}>
              <Copy className="h-4 w-4" aria-hidden="true" /> Dupliquer
            </Button>
          </div>
          {enCours && (
            <p className="mt-2 flex items-start gap-1.5 text-2xs text-ink-muted">
              <Lock className="mt-0.5 h-3 w-3 flex-shrink-0" aria-hidden="true" />
              Des parcours vivent sur ce circuit : ses étapes ne peuvent plus être
              supprimées. Renommer reste possible ; pour arrêter les nouveaux
              départs, désactivez-le.
            </p>
          )}
        </Card>

        {/* Refonte UX, chantier 5 (maquette 6) — la vue graphique du circuit. */}
        <Card variant="glass" padded>
          <h3 className="mb-2 text-sm font-medium text-ink">Vue graphique</h3>
          <CircuitEditeurVisuel
            definition={ouvert}
            onModifier={async (tid, body) => { await action(() => api.workflows.modifierTransition(ouvert.id, tid, body), 'Flèche enregistrée.'); }}
            onConfigurerEnvoi={(tid) => setEnvoiFleche(tid)}
          />
          <div className="mt-4"><CircuitSimulateur definition={ouvert} /></div>
        </Card>

        {/* Les étapes */}
        <Card variant="glass" padded>
          <h3 className="mb-2 text-sm font-medium text-ink">Étapes</h3>
          <div className="space-y-1.5">
            {ouvert.etats.map((e) => (
              <div key={e.id} className="flex items-center gap-2 rounded-r-lg border border-border/60 px-2.5 py-1.5">
                <span className="h-3 w-3 flex-shrink-0 rounded-full"
                  style={{ backgroundColor: e.couleur ?? '#64748b' }} aria-hidden="true" />
                <input defaultValue={e.libelle}
                  onBlur={(ev) => {
                    if (ev.target.value.trim() && ev.target.value !== e.libelle) {
                      void action(() => api.workflows.modifierEtat(ouvert.id, e.id, { libelle: ev.target.value.trim() }));
                    }
                  }}
                  className="min-w-0 flex-1 bg-transparent text-sm text-ink focus:outline-none" />
                {e.estInitial && <Badge variant="info" size="sm">Départ</Badge>}
                {e.estFinal && <Badge variant="neutral" size="sm">Fin</Badge>}
                {e.echeanceJours !== null && (
                  <span className="text-2xs text-ink-muted">J{e.echeanceJours >= 0 ? '+' : ''}{e.echeanceJours}</span>
                )}
                <button type="button" aria-label={`Supprimer l'étape ${e.libelle}`}
                  disabled={enCours}
                  onClick={() => {
                    void (async () => {
                      const ok = await confirm({
                        title: `Supprimer « ${e.libelle} » ?`,
                        description: 'Les flèches qui la touchent partiront avec elle.',
                        confirmLabel: 'Supprimer',
                      });
                      if (ok) void action(() => api.workflows.supprimerEtat(ouvert.id, e.id));
                    })();
                  }}
                  className="text-ink-muted transition-colors hover:text-danger disabled:cursor-not-allowed disabled:opacity-30">
                  <Trash2 className="h-4 w-4" aria-hidden="true" />
                </button>
              </div>
            ))}
          </div>
        </Card>

        {/* Les flèches */}
        <Card variant="glass" padded>
          <h3 className="mb-2 text-sm font-medium text-ink">Flèches (les boutons du parcours)</h3>
          <div className="space-y-1.5">
            {ouvert.transitions.map((t) => (
              <div key={t.id} className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-r-lg border border-border/60 px-2.5 py-1.5 text-sm">
                <span className="text-ink-muted">{etatDe(t.deEtatId)?.libelle}</span>
                <ArrowRight className="h-3.5 w-3.5 flex-shrink-0 text-ink-muted" aria-hidden="true" />
                <span className="text-ink-muted">{etatDe(t.versEtatId)?.libelle}</span>
                <span className="font-medium text-ink">« {t.libelle} »</span>
                <Badge variant="neutral" size="sm">{ROLES.find((r) => r.v === t.roleMinimum)?.l}</Badge>
                {t.serviceRequis && (
                  <Badge variant="info" size="sm">{SERVICES.find((s) => s.v === t.serviceRequis)?.l}</Badge>
                )}
                {t.pieceRequise && <Badge variant="warning" size="sm">Pièce exigée</Badge>}
                {t.envoiConfig && (
                  <Badge variant="info" size="sm">
                    Envoi {t.envoiConfig.canal}{t.envoiConfig.validationRequise ? ' + validation' : ''}
                  </Badge>
                )}
                <button type="button"
                  onClick={() => {
                    if (envoiFleche === t.id) { setEnvoiFleche(null); return; }
                    setEnvoiFleche(t.id);
                    const c = t.envoiConfig;
                    setEcCanal(c?.canal ?? 'interne');
                    setEcSujet(c?.gabaritSujet ?? '');
                    setEcCorps(c?.gabaritCorps ?? '');
                    setEcValidation(c?.validationRequise ?? true);
                    setEcValidateurs(c?.validateurs ?? []);
                  }}
                  className="text-xs text-accent underline-offset-2 hover:underline">
                  {t.envoiConfig ? 'Envoi…' : 'Configurer un envoi'}
                </button>
                <button type="button" aria-label={`Supprimer la flèche ${t.libelle}`}
                  onClick={() => void action(() => api.workflows.supprimerTransition(ouvert.id, t.id))}
                  className="ml-auto text-ink-muted transition-colors hover:text-danger">
                  <Trash2 className="h-4 w-4" aria-hidden="true" />
                </button>
                {envoiFleche === t.id && (
                  <div className="w-full space-y-2 border-t border-border/60 pt-2">
                    <p className="text-xs text-ink-muted">
                      Franchir cette flèche créera un envoi À PRÉPARER (rien ne part tout seul).
                      Gabarits : {'{entite}'}, {'{etape}'}, {'{date}'}, {'{commune}'}.
                    </p>
                    <div className="flex flex-wrap items-center gap-2">
                      <select value={ecCanal} onChange={(ev) => setEcCanal(ev.target.value as 'interne' | 'email' | 'sms')}
                        aria-label="Canal d'envoi" className="h-8 rounded-r-md border border-border bg-surface/60 px-2 text-xs text-ink">
                        <option value="interne">Interne (notification VPC)</option>
                        <option value="email">Email</option>
                        <option value="sms">SMS (passerelle, solde décompté)</option>
                      </select>
                      <label className="flex items-center gap-1.5 text-xs text-ink">
                        <input type="checkbox" checked={ecValidation}
                          onChange={(ev) => setEcValidation(ev.target.checked)} />
                        Validation obligatoire
                      </label>
                    </div>
                    {ecValidation && (
                      <div>
                        <p className="mb-0.5 text-xs text-ink-muted">Validateurs désignés (managers / admins) :</p>
                        <div className="flex flex-wrap gap-1.5">
                          {encadrants.map((u) => (
                            <label key={u.id} className="flex items-center gap-1 rounded-full border border-border/60 px-2 py-0.5 text-xs text-ink">
                              <input type="checkbox" checked={ecValidateurs.includes(u.id)}
                                onChange={(ev) => setEcValidateurs(ev.target.checked
                                  ? [...ecValidateurs, u.id]
                                  : ecValidateurs.filter((x) => x !== u.id))} />
                              {u.label}
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                    <input value={ecSujet} onChange={(ev) => setEcSujet(ev.target.value)}
                      placeholder="Gabarit du sujet — ex : {entite} : {etape}" aria-label="Gabarit du sujet"
                      className="h-8 w-full rounded-r-md border border-border bg-surface/60 px-2 text-xs text-ink" />
                    <textarea value={ecCorps} onChange={(ev) => setEcCorps(ev.target.value)} rows={2}
                      placeholder="Gabarit du message — ex : Le dossier {entite} est passé à l'étape {etape} le {date}. {commune}"
                      aria-label="Gabarit du message"
                      className="w-full rounded-r-md border border-border bg-surface/60 px-2 py-1 text-xs text-ink" />
                    <div className="flex gap-1.5">
                      <Button variant="primary" size="sm"
                        disabled={ecValidation && ecValidateurs.length === 0}
                        onClick={() => void action(async () => {
                          await api.workflows.configurerEnvoi(ouvert.id, t.id, {
                            canal: ecCanal, gabaritSujet: ecSujet, gabaritCorps: ecCorps,
                            validationRequise: ecValidation, validateurs: ecValidation ? ecValidateurs : [],
                          });
                          setEnvoiFleche(null);
                        })}>
                        Enregistrer l'envoi
                      </Button>
                      {t.envoiConfig && (
                        <Button variant="ghost" size="sm"
                          onClick={() => void action(async () => {
                            await api.workflows.configurerEnvoi(ouvert.id, t.id, null);
                            setEnvoiFleche(null);
                          })}>
                          Retirer l'envoi
                        </Button>
                      )}
                    </div>
                  </div>
                )}
                {envoiFleche === t.id && (
                  <div className="w-full space-y-2 border-t border-border/60 pt-2">
                    <p className="text-xs text-ink-muted">
                      Franchir cette flèche créera un envoi À PRÉPARER (rien ne part tout seul).
                      Gabarits : {'{entite}'}, {'{etape}'}, {'{date}'}, {'{commune}'}.
                    </p>
                    <div className="flex flex-wrap items-center gap-2">
                      <select value={ecCanal} onChange={(ev) => setEcCanal(ev.target.value as 'interne' | 'email' | 'sms')}
                        aria-label="Canal d'envoi" className="h-8 rounded-r-md border border-border bg-surface/60 px-2 text-xs text-ink">
                        <option value="interne">Interne (notification VPC)</option>
                        <option value="email">Email</option>
                        <option value="sms">SMS (passerelle, solde décompté)</option>
                      </select>
                      <label className="flex items-center gap-1.5 text-xs text-ink">
                        <input type="checkbox" checked={ecValidation}
                          onChange={(ev) => setEcValidation(ev.target.checked)} />
                        Validation obligatoire
                      </label>
                    </div>
                    {ecValidation && (
                      <div>
                        <p className="mb-0.5 text-xs text-ink-muted">Validateurs désignés (managers / admins) :</p>
                        <div className="flex flex-wrap gap-1.5">
                          {encadrants.map((u) => (
                            <label key={u.id} className="flex items-center gap-1 rounded-full border border-border/60 px-2 py-0.5 text-xs text-ink">
                              <input type="checkbox" checked={ecValidateurs.includes(u.id)}
                                onChange={(ev) => setEcValidateurs(ev.target.checked
                                  ? [...ecValidateurs, u.id]
                                  : ecValidateurs.filter((x) => x !== u.id))} />
                              {u.label}
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                    <input value={ecSujet} onChange={(ev) => setEcSujet(ev.target.value)}
                      placeholder="Gabarit du sujet — ex : {entite} : {etape}" aria-label="Gabarit du sujet"
                      className="h-8 w-full rounded-r-md border border-border bg-surface/60 px-2 text-xs text-ink" />
                    <textarea value={ecCorps} onChange={(ev) => setEcCorps(ev.target.value)} rows={2}
                      placeholder="Gabarit du message — ex : Le dossier {entite} est passé à l'étape {etape} le {date}. {commune}"
                      aria-label="Gabarit du message"
                      className="w-full rounded-r-md border border-border bg-surface/60 px-2 py-1 text-xs text-ink" />
                    <div className="flex gap-1.5">
                      <Button variant="primary" size="sm"
                        disabled={ecValidation && ecValidateurs.length === 0}
                        onClick={() => void action(async () => {
                          await api.workflows.configurerEnvoi(ouvert.id, t.id, {
                            canal: ecCanal, gabaritSujet: ecSujet, gabaritCorps: ecCorps,
                            validationRequise: ecValidation, validateurs: ecValidation ? ecValidateurs : [],
                          });
                          setEnvoiFleche(null);
                        })}>
                        Enregistrer l'envoi
                      </Button>
                      {t.envoiConfig && (
                        <Button variant="ghost" size="sm"
                          onClick={() => void action(async () => {
                            await api.workflows.configurerEnvoi(ouvert.id, t.id, null);
                            setEnvoiFleche(null);
                          })}>
                          Retirer l'envoi
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="mt-3 grid gap-2 border-t border-border/60 pt-3 sm:grid-cols-2 lg:grid-cols-3">
            <Field label="Depuis l'étape" htmlFor="tr-de">
              <select id="tr-de" value={trDe} onChange={(e) => setTrDe(e.target.value)} className={CHAMP}>
                <option value="">—</option>
                {ouvert.etats.map((e) => <option key={e.id} value={e.id}>{e.libelle}</option>)}
              </select>
            </Field>
            <Field label="Vers l'étape" htmlFor="tr-vers">
              <select id="tr-vers" value={trVers} onChange={(e) => setTrVers(e.target.value)} className={CHAMP}>
                <option value="">—</option>
                {ouvert.etats.filter((e) => e.id !== trDe).map((e) => <option key={e.id} value={e.id}>{e.libelle}</option>)}
              </select>
            </Field>
            <Field label="Texte du bouton" htmlFor="tr-lib">
              <input id="tr-lib" value={trLibelle} onChange={(e) => setTrLibelle(e.target.value)}
                placeholder="Ex : Autoriser" className={CHAMP} />
            </Field>
            <Field label="Qui peut cliquer" htmlFor="tr-role">
              <select id="tr-role" value={trRole} onChange={(e) => setTrRole(e.target.value)} className={CHAMP}>
                {ROLES.map((r) => <option key={r.v} value={r.v}>{r.l}</option>)}
              </select>
            </Field>
            <Field label="Service exigé (hors admins)" htmlFor="tr-service">
              <select id="tr-service" value={trService} onChange={(e) => setTrService(e.target.value)} className={CHAMP}>
                {SERVICES.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
              </select>
            </Field>
            <label className="flex items-end gap-2 pb-2.5 text-sm text-ink">
              <input type="checkbox" checked={trPiece} onChange={(e) => setTrPiece(e.target.checked)}
                className="h-4 w-4 rounded border-border" />
              Pièce exigée (PDF/photo)
            </label>
          </div>
          <Button variant="primary" size="sm" className="mt-2"
            disabled={!trDe || !trVers || !trLibelle.trim()}
            onClick={() => void action(() => api.workflows.ajouterTransition(ouvert.id, {
              deEtatId: trDe, versEtatId: trVers, libelle: trLibelle.trim(),
              roleMinimum: trRole, serviceRequis: trService || null, pieceRequise: trPiece,
            }), 'Flèche ajoutée.').then(() => { setTrDe(''); setTrVers(''); setTrLibelle(''); setTrPiece(false); })}>
            <Plus className="h-4 w-4" aria-hidden="true" /> Ajouter la flèche
          </Button>
        </Card>
      </div>
    );
  }

  // ── Vue liste ──────────────────────────────────────────────────────────────
  return (
    <div className="space-y-3">
      {dialogElement}
      {error && <Alert variant="error" icon={AlertCircle}>{error}</Alert>}
      {msg && <Alert variant="success" icon={Check}>{msg}</Alert>}

      <div className="flex items-center justify-between gap-2">
        <h1 className="flex items-center gap-2 text-base font-semibold text-ink">
          <GitBranch className="h-5 w-5" aria-hidden="true" /> Circuits d'étapes
        </h1>
        <Button variant="primary" size="sm" onClick={() => setChoixPreset(!choixPreset)}>
          <Plus className="h-4 w-4" aria-hidden="true" /> Ajouter depuis un modèle
        </Button>
      </div>

      <p className="flex items-start gap-1.5 text-xs text-ink-muted">
        <Info className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
        Un circuit décrit les étapes d'un parcours (devis, projet…) : qui avance,
        avec quelle pièce, vers quelle étape. Dupliquez un modèle puis adaptez
        votre copie — le modèle d'origine reste intact.
      </p>

      {choixPreset && (
        <Card variant="glass" padded>
          <h3 className="mb-2 text-sm font-medium text-ink">Modèles disponibles</h3>
          <div className="grid gap-2 sm:grid-cols-2">
            {presets.map((p) => (
              <button key={p.code} type="button"
                onClick={() => {
                  setChoixPreset(false);
                  void action(() => api.workflows.depuisPreset(p.code),
                    `« ${p.libelle} » ajouté — c'est votre copie, adaptez-la.`);
                }}
                className="rounded-r-lg border border-border p-3 text-left transition-colors hover:border-accent/50">
                <p className="text-sm font-medium text-ink">{p.libelle}</p>
                <p className="mt-0.5 text-xs text-ink-muted">{p.description}</p>
                <p className="mt-1 text-2xs text-ink-muted">{p.etats} étapes · {p.transitions} flèches</p>
              </button>
            ))}
          </div>
        </Card>
      )}

      {definitions.length === 0 && !choixPreset ? (
        <Card variant="glass" padded className="text-center text-sm text-ink-muted">
          Aucun circuit pour l'instant — commencez par dupliquer un modèle.
        </Card>
      ) : (
        <div className="space-y-2">
          {definitions.map((d) => (
            <Card key={d.id} variant="glass" padded
              className="cursor-pointer transition-colors hover:border-accent/40"
              onClick={() => setOuvert(d)}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-ink">{d.libelle}</p>
                  <p className="text-xs text-ink-muted">
                    {d.etats.length} étapes · {d.transitions.length} flèches
                  </p>
                </div>
                <div className="flex flex-shrink-0 items-center gap-1.5">
                  {d.instances > 0 && <Badge variant="warning" size="sm">{d.instances} en cours</Badge>}
                  <Badge variant={d.actif ? 'success' : 'neutral'} size="sm">
                    {d.actif ? 'Actif' : 'Désactivé'}
                  </Badge>
                  {d.instances === 0 && (
                    <button type="button" aria-label={`Supprimer ${d.libelle}`}
                      onClick={(ev) => {
                        ev.stopPropagation();
                        void (async () => {
                          const ok = await confirm({
                            title: `Supprimer « ${d.libelle} » ?`,
                            description: 'Aucun parcours en cours — la suppression est définitive.',
                            confirmLabel: 'Supprimer',
                          });
                          if (ok) void action(() => api.workflows.supprimer(d.id), 'Circuit supprimé.');
                        })();
                      }}
                      className="text-ink-muted transition-colors hover:text-danger">
                      <Trash2 className="h-4 w-4" aria-hidden="true" />
                    </button>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
